import React from 'react';
import { Typography } from '@one-thd/sui-atomic-components';
import { DefaultShipping } from '../partials/shipping/DefaultShipping';
import { FULFILLMENT_METHODS } from '../../components/constants';
import {
  getLiveGoodsStock,
  isApplianceDeliveryError,
  isApplianceDeliveryOutOfStock,
  isApplianceDeliveryUnavailable
} from '../../components/helper/utils';
import {
  getApplianceEarliestDeliveryDate,
  getApplianceEarliestDeliveryDateFromSearch,
  getApplianceInventory,
  getBODFSFulfillment,
  getDeliveryService,
  getDisplayableInventory,
  getExcludedStateSth,
  getSTHFulfillment,
  getShippingThreshold,
  getStateCode,
  hasFreeShipping,
  isAppliance,
  isBODFS,
  isBackordered,
  isBackorderedWithDate,
  isBodfsWithQuantityAvailable,
  isBodfsWithQuantityUnknown,
  isDiscontinued,
  isItemGCC,
  isSTH,
  isStateExcluded,
  isSthOutOfStock,
  meetsApplianceDeliveryThreshold
} from './pod-fulfillment-utils';
import { dataModel } from '../../components/dataModel';
import { EmptyShippingMessage } from '../partials/shipping/EmptyShippingMessage';
import {
  BundleFulfillmentMessageShipping
} from '../partials/shipping/BundleFulfillmentMessageShipping';

export const getApplianceScenarios = ({
  availabilityData,
  checkAvailabilityButton,
  customRender,
  enableApplianceDeliveryCharge,
  enableFreeDeliveryForExchange,
  applianceDeliveryThreshold,
  isExchangeCustomer,
  isPuertoRicoZip,
  product
}) => {
  const earliestDeliveryDate = !isPuertoRicoZip
    ? getApplianceEarliestDeliveryDateFromSearch(product)
    : null;

  let meetsThreshold = false;
  let shippingMessage = checkAvailabilityButton;
  let shippingMessageStyle = 'sui-text-success';
  let shippingThreshold = null;
  if (isExchangeCustomer) {
    meetsThreshold = !!enableFreeDeliveryForExchange;
  } else {
    meetsThreshold = meetsApplianceDeliveryThreshold(product?.pricing, applianceDeliveryThreshold);
    // FEATURE SWITCH OVERRIDE
    if (enableApplianceDeliveryCharge) {
      shippingMessageStyle = 'sui-text-primary';
    }
  }

  if (!meetsThreshold) {
    shippingThreshold = applianceDeliveryThreshold;
  }

  if (availabilityData || earliestDeliveryDate) {
    shippingMessage = meetsThreshold ? 'Free' : '';
    const deliveryAvailability = availabilityData?.DeliveryAvailabilityResponse?.deliveryAvailability;
    const showUnavailable = [
      isApplianceDeliveryOutOfStock,
      isApplianceDeliveryUnavailable,
      isApplianceDeliveryError
    ].some((helper) => helper(deliveryAvailability));

    if (showUnavailable) {
      return () => (
        <DefaultShipping
          iconType="close"
          shippingMessage="Unavailable"
          product={product}
          shippingThreshold={shippingThreshold}
          shippingMessageStyle="sui-text-subtle"
        />
      );
    }
    return () => (
      <DefaultShipping
        iconType="success"
        applianceDeliveryDate={
          getApplianceEarliestDeliveryDate(availabilityData) || earliestDeliveryDate
        }
        shippingMessage={enableApplianceDeliveryCharge ? 'Available' : shippingMessage}
        shippingMessageStyle={shippingMessageStyle}
        shippingThreshold={shippingThreshold}
        product={product}
        isAppliance
      />
    );
  }

  return () => (
    <DefaultShipping
      iconType="success"
      shippingMessageStyle={shippingMessageStyle}
      shippingMessage={shippingMessage}
      customRender={customRender}
      applianceQuantity={getApplianceInventory(product)}
      product={product}
      shippingThreshold={shippingThreshold}
      isAppliance={typeof shippingMessage !== 'object'}
    />
  );
};

const getSthOnlyScenarios = ({
  customRender,
  excludedState, hasSameDayFilter, product, isNewFulfillmentPodMessagingEnabled
}) => {
  if (hasSameDayFilter) {
    return null;
  }
  if (excludedState) {
    return () => (
      <DefaultShipping
        excludedState={excludedState}
        customRender={customRender}
        product={product}
      />
    );
  }
  if (isBackorderedWithDate(product) || isBackordered(product)) {
    return () => (
      <DefaultShipping
        iconType="success"
        shippingMessage="Standard"
        customRender={customRender}
        product={product}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      />
    );
  }
  if (isSthOutOfStock(product)) {
    return () => (
      <DefaultShipping
        shippingMessage="Unavailable"
        customRender={customRender}
        product={product}
        shippingMessageStyle="sui-text-subtle"
      />
    );
  }
  return null;
};

const getMixedScenarios = ({
  bodfsFulfillment,
  customRender,
  customer,
  hasSameDayFilter,
  product,
  sthFulfillment,
  isNewFulfillmentPodMessagingEnabled
}) => {
  if (!isSTH(product)) {
    return null;
  }

  const bodfsDisplayInventory = getDisplayableInventory(product, customer, FULFILLMENT_METHODS.BODFS);
  const sthDisplayInventory = getDisplayableInventory(product, customer, FULFILLMENT_METHODS.STH);
  const shippingThreshold = getShippingThreshold(product);
  const excludedState = getExcludedStateSth(product);
  const maxQtyToDisplay = Math.max(bodfsDisplayInventory, sthDisplayInventory);

  if (isBODFS(product) && (excludedState || hasSameDayFilter)) {
    return () => (
      <DefaultShipping
        iconType="success"
        shippingMessage="Scheduled"
        displayQuantity={isNewFulfillmentPodMessagingEnabled ? maxQtyToDisplay : bodfsDisplayInventory}
        customRender={customRender}
        product={product}
        deliveryMessage={bodfsFulfillment?.deliveryMessage}
        hasSameDayFilter={!!hasSameDayFilter}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      />
    );
  }

  const sthScenario = getSthOnlyScenarios({
    customRender,
    excludedState,
    hasSameDayFilter,
    product,
    isNewFulfillmentPodMessagingEnabled
  });

  if (sthScenario) {
    return sthScenario;
  }

  if (isBODFS(product) || !hasSameDayFilter) {
    const displayQuantityValue = isNewFulfillmentPodMessagingEnabled ? maxQtyToDisplay : sthDisplayInventory;
    if (hasFreeShipping(product)) {
      return () => (
        <DefaultShipping
          iconType="success"
          shippingMessageStyle="sui-text-success"
          shippingMessage="Free"
          displayQuantity={displayQuantityValue}
          customRender={customRender}
          product={product}
          deliveryMessage={sthFulfillment?.deliveryMessage}
          isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        />
      );
    }
    if (typeof shippingThreshold === 'number') {
      const shippingMessage = shippingThreshold > 0 ? `Free with $${shippingThreshold} order` : 'Free';
      return () => (
        <DefaultShipping
          iconType="success"
          shippingMessageStyle="sui-text-success"
          shippingMessage={shippingMessage}
          shippingThreshold={shippingThreshold}
          displayQuantity={displayQuantityValue}
          customRender={customRender}
          product={product}
          deliveryMessage={sthFulfillment?.deliveryMessage}
          isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        />
      );
    }
    return () => (
      <DefaultShipping
        iconType="success"
        shippingMessage="Standard"
        displayQuantity={displayQuantityValue}
        customRender={customRender}
        product={product}
        deliveryMessage={sthFulfillment?.deliveryMessage}
        hasSameDayFilter={hasSameDayFilter}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      />
    );
  }
  return null;
};

const getBodfsScenarios = ({
  bodfsFulfillment,
  customRender, customer, product, isNewFulfillmentPodMessagingEnabled
}) => {

  const bodfsDisplayInventory = getDisplayableInventory(product, customer, FULFILLMENT_METHODS.BODFS);
  const sthDisplayInventory = getDisplayableInventory(product, customer, FULFILLMENT_METHODS.STH);
  const maxQtyToDisplayBodfs = Math.max(bodfsDisplayInventory, sthDisplayInventory);

  if (!isBODFS(product)) {
    return null;
  }
  if (isBodfsWithQuantityUnknown(product) || !isBodfsWithQuantityAvailable(product)) {
    return () => (
      <DefaultShipping
        shippingMessage="Unavailable"
        customRender={customRender}
        product={product}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      />
    );
  }
  return () => (
    <DefaultShipping
      iconType="success"
      shippingMessage="Scheduled"
      displayQuantity={isNewFulfillmentPodMessagingEnabled ? maxQtyToDisplayBodfs : bodfsDisplayInventory}
      customRender={customRender}
      product={product}
      deliveryMessage={bodfsFulfillment?.deliveryMessage}
      isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
    />
  );
};

const getUnavailableScenarios = ({ customRender, product, isNewFulfillmentPodMessagingEnabled }) => {
  /* -----not STH and not BODFS means no delivery to customer directly----- */
  if (isDiscontinued(product)) {
    return () => <EmptyShippingMessage customRender={customRender} product={product} />;
  }
  return () => (
    <DefaultShipping
      shippingMessage="Unavailable"
      customRender={customRender}
      product={product}
      shippingMessageStyle="sui-text-subtle"
      isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
    />
  );
};

export const getShippingTemplate = ({
  availabilityData,
  product,
  customer,
  customRender,
  checkAvailabilityButton,
  isPuertoRicoZip,
  enableApplianceDeliveryCharge,
  enableFreeDeliveryForExchange,
  applianceDeliveryThreshold,
  isExchangeCustomer,
  hasSameDayFilter = false,
  bundleFlag,
  showBundleFulfillmentMessage,
  isNewFulfillmentPodMessagingEnabled
}) => {
  const isGCC = isItemGCC(product);

  if (!product || isGCC) {
    return () => (
      <EmptyShippingMessage isGCC={isGCC} customRender={customRender} product={product} />
    );
  }
  /* -----MA Bundle----- */
  if (bundleFlag) {
    return showBundleFulfillmentMessage ? () => <BundleFulfillmentMessageShipping /> : null;
  }

  /* -----LiveGoods----- */
  if (getLiveGoodsStock(product?.fulfillment) === 0) {
    return () => <EmptyShippingMessage customRender={customRender} product={product} />;
  }

  if (isDiscontinued(product)) {
    return () => (
      <DefaultShipping
        shippingMessage="No longer available"
        customRender={customRender}
        product={product}
        shippingMessageStyle="sui-text-subtle"
      />
    );
  }

  /* -----Appliance----- */
  if (isAppliance(product)) {
    return getApplianceScenarios({
      availabilityData,
      checkAvailabilityButton,
      customRender,
      enableApplianceDeliveryCharge,
      enableFreeDeliveryForExchange,
      applianceDeliveryThreshold,
      isExchangeCustomer,
      isPuertoRicoZip,
      product
    });
  }

  if (!getDeliveryService(product)) {
    // TODO: move sthExcludedShipState to isStateExcluded and remove getStateCode after plp speed
    const sthExcludedShipState = product?.fulfillment?.sthExcludedShipState;
    const zipcodeStateCode = sthExcludedShipState || getStateCode(null, 'sth');
    const excludedStateName = isStateExcluded(zipcodeStateCode, 'sth', product?.fulfillment);
    if (excludedStateName) {
      return () => (
        <DefaultShipping
          excludedState={excludedStateName}
          customRender={customRender}
          product={product}
        />
      );
    }
  }

  const bodfsFulfillment = getBODFSFulfillment(product);
  const sthFulfillment = getSTHFulfillment(product);

  return (
    getMixedScenarios({
      bodfsFulfillment,
      customRender,
      customer,
      hasSameDayFilter,
      product,
      sthFulfillment,
      isNewFulfillmentPodMessagingEnabled
    })
    || getBodfsScenarios({
      bodfsFulfillment,
      customRender,
      customer,
      product,
      isNewFulfillmentPodMessagingEnabled
    })
    || getUnavailableScenarios({ customRender, product })
  );
};

getShippingTemplate.dataModel = dataModel;
